import {
  FeatureFlagType,
  isFeatureFlagKey,
  KNOWN_FEATURE_FLAG_KEYS,
} from "encamp-shared/src/featureFlags";
import { type GetFlagMeta, useFeatureFlagMeta } from "featureFlags/useFeatureFlagMeta";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { useMemo } from "react";

/**
 * Filter columns for tenant-scoped grids that want to filter by which
 * feature flags a tenant has enabled. Both filters serialize to the
 * same server-side `hasFlag` omnisearch key via keyMap aliases in the
 * `tenants` resolver — the only difference is which keys are
 * selectable in the chip picker:
 *
 *   - `Modules Enabled` — top-level Module flags only.
 *   - `Flags Enabled`  — every known flag (modules, add-ons, "other").
 *
 * Both filters AND across selected keys (a tenant must have *all* of
 * the selected flags enabled to match), per the `hasFlag` resolver
 * contract.
 *
 * Used by:
 *   - Staff → Feature Flags page (tenant-centric grid)
 *   - Staff → Organizations page (`OrganizationTable`)
 *
 * Keep this in sync with `apps/api/src/resolvers/tenants.ts`
 * `selectToOmnisearchKeyMap` aliases.
 *
 * Pure builder: the metadata accessor is injected so labels/types come from the
 * DB-backed staff `featureFlags` query. Call via {@link useTenantFeatureFlagFilterColumns}
 * from components; the raw builder is exported for tests.
 */
export function buildTenantFeatureFlagFilterColumns(getMeta: GetFlagMeta): FilterKey[] {
  /**
   * Render a feature-flag key as its human label (falling back to the
   * raw key for any value the type guard rejects — e.g. an unknown key
   * persisted from a stale URL filter that we no longer ship).
   */
  const flagKeyToLabel = (key: string): string =>
    isFeatureFlagKey(key) ? getMeta(key).label : key;

  const moduleFlagKeys = [...KNOWN_FEATURE_FLAG_KEYS]
    .filter((k) => getMeta(k).type === FeatureFlagType.Module)
    .sort((a, b) => flagKeyToLabel(a).localeCompare(flagKeyToLabel(b)));

  const allFlagKeysSorted = [...KNOWN_FEATURE_FLAG_KEYS].sort((a, b) =>
    flagKeyToLabel(a).localeCompare(flagKeyToLabel(b))
  );

  return [
    {
      key: "modulesEnabled",
      header: "Modules Enabled",
      filterKeyType: "enumAutocomplete",
      enumValues: moduleFlagKeys,
      enumPresentationFunction: flagKeyToLabel,
    },
    {
      key: "flagsEnabled",
      header: "Flags Enabled",
      filterKeyType: "enumAutocomplete",
      enumValues: allFlagKeysSorted,
      enumPresentationFunction: flagKeyToLabel,
    },
  ];
}

/** Hook wrapper — sources flag metadata from the staff `featureFlags` query. */
export function useTenantFeatureFlagFilterColumns(): FilterKey[] {
  const { getMeta } = useFeatureFlagMeta();
  return useMemo(() => buildTenantFeatureFlagFilterColumns(getMeta), [getMeta]);
}
